import * as moment from 'moment';
import * as React from 'react';
import EditorProps from '../../components/editorinterface';
import DatePicker from 'react-datepicker';
import {Checkbox, FormGroup, InputGroup, Glyphicon, Button} from 'react-bootstrap';
import {default as NumberFormat} from 'react-number-format';
import 'react-datepicker/dist/react-datepicker.css';
import './index.css';

const dateFormat = "YYYY-MM-DD";
const timeFormat = "HH:mm";
const timeIntervals = 15;

type DateValue = {
  value: string,
  isAllDay: 1 | 0
};

interface DateTimeInputFieldProps extends EditorProps {
  onValueChange: (date : moment.Moment | null, event? : React.SyntheticEvent<any> | undefined) => void;
  onFormatChange: (currentFormat : string, isAllDay : boolean) => void;
  // needed to trigger datepicker display if used as a custom input
  enterEditMode: () => void;
  exitEditMode: () => void;
}
/**
 * CURRENT LIMITATION
 * Using this field as a custom input element to the date picker does not give us control
 * on responding to keyboard control events. We need to use display the datepicker separately
 * and manage the events that will toggle its visible status.
 * Inline comes with an additional complexity - z index must be managed for the datepicker
 * when displaying it or hiding it
 */
class DateTimeInputField extends React.Component<DateTimeInputFieldProps> {

  constructor(props : DateTimeInputFieldProps) {
        super(props);
    this.onInputChange = this.onInputChange.bind(this);
        this.onAllDayChange = this.onAllDayChange.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    }

  render(): JSX.Element {
    const props = this.props;
    const time = getNonNullDateValue(props.value);
    const currentFormat = getDateTimeFormat(this.props.field.timeAndDate, !!time.isAllDay);

    return (<FormGroup>
      <InputGroup>
        <NumberFormat value={time.value} format={getHashInputFormat(currentFormat)} placeholder={currentFormat} mask={getNumberFormatMask(currentFormat)} className="form-control" onChange={this.onInputChange} onKeyDown={this.onKeyDown} onClick={props.enterEditMode} onFocus={props.enterEditMode} disabled={1==props.field.readOnly}/>
        { (props.field.timeAndDate) ?
          <InputGroup.Addon>
            <Checkbox
              checked={!!time.isAllDay}
              onChange={this.onAllDayChange}
              inline
              tabIndex={-1}>
                is all day
            </Checkbox>
          </InputGroup.Addon>
          : null
        }
        <InputGroup.Button>
          <Button onClick={this.onClear} tabIndex={-1}>
            <Glyphicon glyph="remove"/>
          </Button>
        </InputGroup.Button>
      </InputGroup>
    </FormGroup>);
  }

  private onAllDayChange(e : any) {
    const isAllDay = e.target.checked;
    this.props.onFormatChange(getDateTimeFormat(this.props.field.timeAndDate, isAllDay), isAllDay);
  }

  private onClear(e : any) {
    this.props.onValueChange(null);
  }

  private cancelEdit() {
    const props = this.props;
    if (props.value === null || props.value.value === null || props.value.value === '') {
      props.onValueChange(null);
    } else {
      const currentFormat = getDateTimeFormat(props.field.timeAndDate, props.value.isAllDay);
      const date = moment(props.value.value, currentFormat);
      props.onValueChange(date);
    }
  }

  private onKeyDown(e : any) {
    if (e.keyCode == 13 || e.keyCode == 27 || e.keyCode == 9) // enter, escape or tab
      this.cancelEdit(); // ensure the content is valid
    else
      this.props.enterEditMode();
    }

  private onInputChange(e : any): any {
    const value = e.target.value;
    const props = this.props;
    const currentFormat = getDateTimeFormat( props.field.timeAndDate, props.value != null ? props.value.isAllDay : false);
    let counter = 0; // used to with actual data (numbers such as YY MM DD, etc.) keyed in
    let offset = 0; // used to deal with spaces and separators (e.g. columns or dashes)
    if (value !== '') {
      // If value is not empty, check its validity
      // Trigger an action only on a complete & valid value
      for (let i = 0; i < currentFormat.length; i++) {
        if (currentFormat[i].match(/[a-z]/gi)) {
          if (value[i].match(/[0-9]/))
            counter++;
        }
        else
          offset++;
      }
      if (counter + offset === currentFormat.length) {
        const date = moment(value, currentFormat);
        if (date.isValid())
          props.onValueChange(date);
        }
      }
      else
      // If value is emptied by user, assume he wants to clear it
      this.props.onValueChange(null);
    }
}

type DateEditorState = {
  showPicker: boolean
}

class DateEditor extends React.Component<EditorProps, DateEditorState> {
  constructor(props : EditorProps) {
    super(props);
    this.onValueChange = this.onValueChange.bind(this);
    this.onFormatChange = this.onFormatChange.bind(this);
    this.enterEditMode = this.enterEditMode.bind(this);
    this.exitEditMode = this.exitEditMode.bind(this);
    this.state = {showPicker: false};
  }

	public render() {
    const time = getNonNullDateValue(this.props.value);
    const currentFormat = getDateTimeFormat(this.props.field.timeAndDate, !!time.isAllDay);
    const showTime = (!!this.props.field.timeAndDate && !time.isAllDay)
      ? {
        showTimeSelect: true,
        timeFormat,
        timeIntervals
      }
      : null;

    const dateTimeInputFieldProps = {
      ...this.props,
      isAllDay: !!time.isAllDay,
      onValueChange: this.onValueChange,
      onFormatChange: this.onFormatChange,
      enterEditMode: this.enterEditMode,
      exitEditMode: this.exitEditMode
    }

		return (
      <div>
        <DateTimeInputField {...dateTimeInputFieldProps}/>
        {this.state.showPicker &&
          <DatePicker
            selected={getSelectedDate(time.value, currentFormat)}
            dateFormat={currentFormat}
            showMonthDropdown
            showYearDropdown
            onChange={this.onValueChange}
            onClickOutside={this.exitEditMode}
            {...showTime}
            inline
          />}
      </div>
		);
	}

  private enterEditMode() {
    if (!this.state.showPicker)
      this.setState({showPicker: true});
	}

  private exitEditMode() {
    if (this.state.showPicker)
      this.setState({showPicker: false});
	}

  private onValueChange(date : moment.Moment | null, event? : React.SyntheticEvent<any> | undefined) {
    const time = getNonNullDateValue(this.props.value);
    const currentFormat = getDateTimeFormat(this.props.field.timeAndDate, !!time.isAllDay);
    this.props.onChange(
      this.props.field.id, date != null
      ? {
        value: date.format(currentFormat),
        isAllDay: time.isAllDay
	}
      : {
        value: '',
        isAllDay: time.isAllDay
      });
    this.exitEditMode();
  }

  private onFormatChange(currentFormat : string, isAllDay : boolean) {
    this.props.onChange(this.props.field.id, applyCheckboxToDateValue(isAllDay, this.props.value));
	}

}

function getNonNullDateValue(value : DateValue | null): DateValue {
  if (value === null) {
		return {value: '', isAllDay: 0};
	}
	return value;
}

function applyCheckboxToDateValue(checked : boolean, originalValue : DateValue | null): (DateValue | null) {
  const isAllDay = (checked) ? 1 : 0;
  return(originalValue === null)
    ? {
      value: '',
      isAllDay
	}
    : {
      ...originalValue,
      isAllDay
    };
}

function getHashInputFormat(format : string): string {
  return format.replace(/[a-z]/gi, "#");
}

function getSelectedDate(initialDatePickerValue : string, format : string) {
  if (initialDatePickerValue === null || initialDatePickerValue === '')
    return moment();
  else
    return moment(initialDatePickerValue, format);
	}

function getNumberFormatMask(format : string): string | string[]{
  var mask = [];
  for (let char of format) {
    if (char.match(/[a-z]/gi)) {
      mask.push(char);
	}
  }
  return mask;
}

function getDateTimeFormat(timeAndDate : boolean, isAllDay : boolean): string {
  return !timeAndDate || timeAndDate && isAllDay
    ? dateFormat
    : dateFormat + " " + timeFormat;
}

export default DateEditor;



// WEBPACK FOOTER //
// ./src/components/dateeditor/index.tsx